import { PROFIL_STANDARD, profilLesen, type Profil } from '@uep/shared';
import { api } from './api';
import { METAS, db, metaSchreiben } from './db';
import { inDieOutbox } from './outbox';
import type { LokalesProfil } from './typen';

/**
 * Persönliches Profil — lokal führend, danach zum Server (CLAUDE.md §3.4).
 *
 * Das Profil wird beim Anlegen eines Protokolls gebraucht, und das passiert vor
 * Ort ohne Netz. Deshalb liegt es wie alles andere in IndexedDB und geht über
 * die Outbox nach oben. Der Serverdatensatz gehört dem Nutzer allein
 * (`allow.owner()`), sodass dasselbe Profil auf jedem Gerät ankommt.
 *
 * Diese Datei stößt den Sync bewusst **nicht** selbst an: `pull.ts` holt hier
 * das Profil, und `sync.ts` importiert `pull.ts` — ein Import von `./sync`
 * ergäbe einen Zyklus. Den Anstoß gibt die aufrufende Seite.
 */

const jetzt = (): string => new Date().toISOString();

const alsFlagge = (wert: boolean): 0 | 1 => (wert ? 1 : 0);

/** Übersetzt den lokalen Datensatz in die geprüfte Profilform. */
export const alsProfil = (eintrag: LokalesProfil | null | undefined): Profil | null => {
  if (!eintrag) return null;
  return profilLesen({
    anzeigename: eintrag.anzeigename,
    funktion: eintrag.funktion,
    firma: eintrag.firma,
    telefon: eintrag.telefon,
    email: eintrag.email,
    standardRolle: eintrag.standardRolle,
    alsBeteiligterEintragen: eintrag.alsBeteiligterEintragen === 1,
    erhaeltProtokoll: eintrag.erhaeltProtokoll === 1,
  });
};

/** Das eigene Profil, oder `null`, solange keines hinterlegt ist. */
export const meinProfil = async (nutzerSchluessel: string): Promise<Profil | null> =>
  alsProfil(await db.profile.get(nutzerSchluessel));

/**
 * Speichert das Profil und merkt es zum Senden vor.
 *
 * Die Gruppe ist der Nutzerschlüssel: Mehrfaches Speichern hintereinander wird
 * dadurch strikt der Reihe nach gesendet, und der letzte Stand gewinnt.
 */
export const profilSpeichern = async (
  nutzerSchluessel: string,
  werte: Profil,
): Promise<LokalesProfil> => {
  const vorhanden = await db.profile.get(nutzerSchluessel);

  const eintrag: LokalesProfil = {
    id: nutzerSchluessel,
    anzeigename: werte.anzeigename.trim(),
    funktion: werte.funktion.trim(),
    firma: werte.firma.trim(),
    telefon: werte.telefon.trim(),
    email: werte.email.trim(),
    standardRolle: werte.standardRolle.trim(),
    alsBeteiligterEintragen: alsFlagge(werte.alsBeteiligterEintragen),
    erhaeltProtokoll: alsFlagge(werte.erhaeltProtokoll),
    lokalGeaendertAm: jetzt(),
    synchronisiert: 0,
  };

  await db.profile.put(eintrag);
  await inDieOutbox({
    entitaet: 'Profil',
    operation: vorhanden ? 'aendern' : 'anlegen',
    entitaetId: nutzerSchluessel,
    gruppe: `Profil:${nutzerSchluessel}`,
  });

  return eintrag;
};

/**
 * Holt das Profil vom Server.
 *
 * Ein lokal noch nicht gesendeter Stand wird nicht überschrieben — sonst ginge
 * eine Änderung verloren, die vor Ort ohne Netz gemacht wurde.
 *
 * Nebenbei verschwinden Profile anderer Personen: Auf einem geteilten Tablet
 * hat der nächste Nutzer nichts mit den Angaben des vorherigen zu tun.
 */
export const profilPullen = async (nutzerSchluessel: string): Promise<boolean> => {
  await db.profile.where('id').notEqual(nutzerSchluessel).delete();

  const lokal = await db.profile.get(nutzerSchluessel);
  if (lokal && lokal.synchronisiert === 0) return false;

  const antwort = await api().models.Profil.get({ id: nutzerSchluessel });
  const fern = antwort.data;
  if (!fern) return false;

  await db.profile.put({
    id: nutzerSchluessel,
    anzeigename: fern.anzeigename ?? '',
    funktion: fern.funktion ?? '',
    firma: fern.firma ?? '',
    telefon: fern.telefon ?? '',
    email: fern.email ?? '',
    standardRolle: fern.standardRolle ?? PROFIL_STANDARD.standardRolle,
    alsBeteiligterEintragen: alsFlagge(fern.alsBeteiligterEintragen ?? true),
    erhaeltProtokoll: alsFlagge(fern.erhaeltProtokoll ?? false),
    lokalGeaendertAm: fern.updatedAt ?? jetzt(),
    synchronisiert: 1,
  });

  return true;
};

/** Merkt sich, wer angemeldet ist — der Sync braucht den Schlüssel ohne Cognito. */
export const nutzerSchluesselMerken = async (nutzerSchluessel: string | null): Promise<void> => {
  await metaSchreiben(METAS.nutzerSchluessel, nutzerSchluessel);
};
